<template>
  <div class="collection-detail">
    <!-- Page Content Container -->
    <div class="page-content">
      <!-- Page Header Area -->
      <div class="page-header">
        <div class="header-content">
          <div class="left-section">
            <v-btn
              icon
              variant="text"
              size="medium"
              class="back-btn"
              @click="goBack">
              <Icon name="arrow-left" variant="solid" size="20" color="default"/>
            </v-btn>
            <h1 class="page-title">
              {{ collection?.name || 'Collection' }}
            </h1>
          </div>

          <div class="header-buttons">
            <v-btn
              icon
              size="small"
              variant="text"
              color="primary"
              class="call-btn mank-btn-icon mr-4 mank-btn-icon-md"
              title="Delete collection"
              @click="openDeleteCollectionDialog">
              <Icon name="trash" variant="solid" color="default" size="16"/>
            </v-btn>
            <v-btn
              icon
              @click="editCollection"
              class="mank-btn-icon mr-4">
              <Icon name="edit" variant="solid" size="20" color="default"/>
            </v-btn>
            <v-btn
              color="primary"
              class="mank-btn"
              @click="createSource"
              elevation="0">
              <Icon name="plus" variant="solid" color="onPrimary"/>
              Create Source
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Content -->
      <div class="content-section">
        <!-- Loading State -->
        <div v-if="loading" class="loading-container">
          <v-progress-circular
            indeterminate
            color="primary"
            size="48"/>
          <p class="loading-text">
            Loading collection details...
          </p>
        </div>

        <!-- Main Content -->
        <div v-else class="main-content">
          <!-- Collection Info Table -->
          <div class="collection-info-table">
            <div class="info-rows">
              <div class="info-row">
                <span class="label">Agent ID</span>
                <div class="value-with-icon">
                  <span class="value">{{ collection?.id || '-' }}</span>
                  <v-btn
                    icon
                    size="small"
                    variant="text"
                    @click="copyId"
                    class="copy-btn">
                    <Icon name="copy" variant="solid" size="16" color="default"/>
                  </v-btn>
                </div>
              </div>

              <div class="info-row">
                <span class="label">Date Created</span>
                <span class="value">{{ formatDate(collection?.created_at) }}</span>
              </div>

              <div class="info-row">
                <span class="label">Last Updated</span>
                <span class="value">{{ formatDate(collection?.updated_at) || '-' }}</span>
              </div>
            </div>
          </div>

          <div class="collection-detail-metrics">
            <!-- Data Cards Section -->
            <div class="data-cards">
              <div class="data-card">
                <div class="card-header">
                  <h3 class="card-title">
                    Documents
                  </h3>
                </div>
                <div class="card-content">
                  <span class="card-value">{{ stats.documents }}</span>
                </div>
              </div>

              <div class="data-card">
                <div class="card-header">
                  <h3 class="card-title">
                    Chunks
                  </h3>
                </div>
                <div class="card-content">
                  <span class="card-value">{{ stats.chunks }}</span>
                </div>
              </div>

              <div class="data-card">
                <div class="card-header">
                  <h3 class="card-title">
                    Vectors
                  </h3>
                </div>
                <div class="card-content">
                  <span class="card-value">{{ stats.vectors }}</span>
                </div>
              </div>
            </div>

            <!-- Sources Accordion -->
            <div
              v-for="source in sources"
              :key="source.id"
              class="source-accordion">
              <div class="accordion-header">
                <div class="source-info">
                  <div class="source-title-section">
                    <div class="title-with-tag">
                      <h3 class="source-name">
                        {{ source.name }}
                      </h3>
                      <v-chip
                        size="small"
                        color="primary"
                        class="source-tag">
                        {{ source.source_type }}
                      </v-chip>
                    </div>
                    <span class="source-id">{{ source.id }}</span>
                  </div>

                  <div class="source-actions">
                    <v-btn
                      icon
                      size="small"
                      variant="text"
                      @click="deleteSource(source)"
                      class="action-btn">
                      <Icon name="trash" variant="solid" size="16" color="default"/>
                    </v-btn>

                    <v-btn
                      icon
                      size="small"
                      variant="text"
                      @click="editSource(source)"
                      class="action-btn">
                      <Icon name="edit" variant="solid" size="16" color="default"/>
                    </v-btn>
                  </div>
                </div>
              </div>

              <div class="accordion-content">
                <div class="source-details">
                  <div class="detail-row">
                    <span class="detail-label">Last Updated</span>
                    <span class="detail-value">{{ formatDate(source.updated_at) }}</span>
                  </div>

                  <div class="detail-row" v-if="source.source_type === 'web' && source.crawl_depth">
                    <span class="detail-label">Crawl Depth</span>
                    <span class="detail-value">{{ source.crawl_depth }}</span>
                  </div>

                  <div class="detail-row" v-if="source.source_type === 'web' && source.urls && source.urls.length > 0">
                    <span class="detail-label">URLS</span>
                    <div class="urls-section">
                      <div class="url-numbers">
                        <span
                          v-for="(url, index) in source.urls"
                          :key="index"
                          class="url-number">
                          {{ index + 1 }}.
                        </span>
                      </div>
                      <div class="url-list">
                        <a
                          v-for="url in source.urls"
                          :key="url"
                          :href="url"
                          target="_blank"
                          class="url-link">
                          {{ url }}
                        </a>
                      </div>
                    </div>
                  </div>

                  <div class="detail-row" v-if="source.source_type === 'document' && source.assets && source.assets.length > 0">
                    <span class="detail-label">Documents</span>
                    <div class="documents-section">
                      <div
                        v-for="asset in source.assets"
                        :key="asset.id"
                        class="document-item">
                        <span class="document-name">{{ asset.name || asset.filename }}</span>
                        <span class="document-size" v-if="asset.size">{{ formatFileSize(asset.size) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Success Toast -->
    <div
      v-if="showSuccessToast"
      class="success-toast">
      <div class="toast-content">
        <div class="toast-icon">
          <v-icon color="white" size="24">
            mdi-check
          </v-icon>
        </div>
        <div class="toast-text">
          <h4 class="toast-title">
            "{{ collectionName }}" Edited Successfully!
          </h4>
          <p class="toast-message">
            You have successfully updated the project name.
          </p>
        </div>
        <v-btn
          icon
          size="small"
          variant="text"
          @click="closeToast"
          class="toast-close">
          <v-icon size="20" color="rgba(255,255,255,0.7)">
            mdi-close
          </v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Create Source Drawer -->
    <CreateSourceDrawer
      :visible="showCreateSourceDrawer"
      :collection-id="collection?.id"
      :source="sourceToEdit"
      @update:visible="showCreateSourceDrawer = $event"
      @source-created="onSourceCreated"
      @source-updated="onSourceUpdated"/>

    <!-- Edit Collection Drawer -->
    <CreateCollectionDrawer
      :visible="showEditCollectionDrawer"
      :collection="collection"
      @close="closeEditCollectionDrawer"
      @updated="onCollectionUpdated"/>

    <!-- Delete source -->
    <DeleteConfirmationModal
      :visible="showDeleteModal"
      @confirm="confirmDeleteSource"
      @cancel="closeDeleteModal"/>

    <!-- Delete collection -->
    <DeleteConfirmationModal
      :visible="showDeleteCollectionModal"
      @confirm="confirmDeleteCollection"
      @cancel="cancelDeleteCollection"/>
  </div>
</template>

<script>
import CollectionService from '../services/CollectionService';
import RagService from '../services/RagService';
import Icon from '../components/Icon.vue';
import CreateSourceDrawer from '../components/CreateSourceDrawer.vue';
import CreateCollectionDrawer from '../components/CreateCollectionDrawer.vue';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal.vue';

export default {
  name: 'CollectionDetail',
  components: { Icon, CreateSourceDrawer, CreateCollectionDrawer, DeleteConfirmationModal },
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      collection: null,
      showSuccessToast: false,
      showCreateSourceDrawer: false,
      showEditCollectionDrawer: false,
      showDeleteModal: false,
      showDeleteCollectionModal: false,
      sourceToEdit: null,
      sourceToDelete: null,
      stats: {
        documents: 0,
        chunks: 0,
        vectors: 0,
      },
      sources: [],
    };
  },
  computed: {
    collectionName() {
      return this.collection?.name || 'Loading...';
    },
  },
  async mounted() {
    await this.fetchCollection();
  },
  methods: {
    async fetchCollection() {
      this.loading = true;
      try {
        const collectionId = this.id || this.$route.params.id;
        const response = await CollectionService.getCollectionById(collectionId);

        this.collection = response.data || response;
        this.sources = response.data.rags;
        // Update stats based on collection data
        this.stats = {
          documents: this.collection.doc_count || 0,
          chunks: this.collection.chunk_count || 0,
          vectors: this.collection.vector_count || 0,
        };
      } catch (error) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          title: 'Error',
          message: 'Failed to load collection details',
        });

        // Fallback data for error case
        this.collection = {
          id: this.id || this.$route.params.id,
          name: 'Collection not found',
          created_at: null,
          updated_at: null,
        };
      } finally {
        this.loading = false;
      }
    },

    goBack() {
      this.$router.go(-1);
    },

    openDeleteCollectionDialog() {
      if (!this.collection?.id) return;
      this.showDeleteCollectionModal = true;
    },
    cancelDeleteCollection() {
      this.showDeleteCollectionModal = false;
    },
    async confirmDeleteCollection() {
      if (!this.collection?.id) {
        this.showDeleteCollectionModal = false;
        return;
      }
      try {
        await CollectionService.deleteCollection(this.collection.id);

        this.$store.dispatch('ui/showNotification', {
          type: 'success',
          title: 'Success',
          message: 'Collection deleted successfully',
        });

        this.$router.push({ name: 'RAG' });
      } catch (error) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          title: 'Error',
          message: 'Failed to delete collection',
        });
      } finally {
        this.showDeleteCollectionModal = false;
      }
    },

    editCollection() {
      // Open edit collection drawer
      this.showEditCollectionDrawer = true;
    },

    createSource() {
      // Clear source to edit and open drawer for create mode
      this.sourceToEdit = null;
      this.showCreateSourceDrawer = true;
    },

    async copyId() {
      if (!this.collection?.id) return;

      try {
        await navigator.clipboard.writeText(this.collection.id);
        this.$store.dispatch('ui/showNotification', {
          type: 'success',
          title: 'Success',
          message: 'Collection ID copied to clipboard',
        });
      } catch (error) {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = this.collection.id;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);

        this.$store.dispatch('ui/showNotification', {
          type: 'success',
          title: 'Success',
          message: 'Collection ID copied to clipboard',
        });
      }
    },

    deleteSource(source) {
      if (!source?.id) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          title: 'Error',
          message: 'Source ID not found',
        });
        return;
      }

      this.sourceToDelete = source;
      this.showDeleteModal = true;
    },

    async confirmDeleteSource() {
      try {
        await RagService.deleteRag(this.sourceToDelete.id);

        this.$store.dispatch('ui/showNotification', {
          type: 'success',
          title: 'Success',
          message: 'Source deleted successfully',
        });

        await this.fetchCollection();
      } catch (error) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          title: 'Error',
          message: 'Failed to delete source',
        });
      } finally {
        this.closeDeleteModal();
      }
    },

    closeDeleteModal() {
      this.showDeleteModal = false;
      this.sourceToDelete = null;
    },

    editSource(source) {
      // Set source to edit and open drawer
      this.sourceToEdit = { ...source };
      this.showCreateSourceDrawer = true;
    },

    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    },

    closeToast() {
      this.showSuccessToast = false;
    },

    formatFileSize(bytes) {
      if (!bytes) return '';
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      return `${(bytes / (1024 ** i)).toFixed(1)} ${sizes[i]}`;
    },

    onSourceCreated() {
      this.fetchCollection();
      this.sourceToEdit = null;
    },

    onSourceUpdated(updatedSource) {
      // Update source in the sources list
      const index = this.sources.findIndex((s) => s.id === updatedSource.id);
      if (index !== -1) {
        this.sources[index] = {
          ...this.sources[index],
          ...updatedSource,
        };
      }

      this.fetchCollection();
      this.sourceToEdit = null;
    },

    onCollectionUpdated(updatedCollection) {
      this.collection = {
        ...this.collection,
        ...updatedCollection,
      };

      this.showEditCollectionDrawer = false;
    },

    closeEditCollectionDrawer() {
      this.showEditCollectionDrawer = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../styles/variables.scss';
@import '../styles/colors.scss';

.page-header .header-buttons {
  .action-btn {
    width: 32px;
    height: 32px;
    color: $light-foreground-1;

    &:hover {
      background-color: rgba(247, 248, 251, 0.8);
    }
  }

  .create-source-btn {
    height: 32px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-weight: 600;
    font-size: $body-3-size;
    text-transform: none;
    letter-spacing: normal;
    padding: 6px 12px;
  }
}

.content-section {
  display: flex;
  flex-direction: column;
  align-self: stretch;
  gap: 10px;
  flex: 1;
  overflow: hidden;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 16px;

  .loading-text {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-weight: 400;
    font-size: $body-3-size;
    color: $light-foreground-2;
    margin: 0;
  }
}

.main-content {
  align-self: stretch;
  gap: 10px;
  overflow-y: auto;
}

.collection-info-table {
  display: flex;
  flex-direction: column;
  align-self: stretch;
  gap: 24px;
  padding: 24px;

  .info-rows {
    display: flex;
    justify-content: stretch;
    align-items: stretch;
    align-self: stretch;
    gap: 24px;

    .info-row {
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: 4px;
      flex: 1;

      .label {
        font-family: 'Plus Jakarta Sans', sans-serif;
        font-weight: 400;
        font-size: $body-3-size;
        line-height: 1.2;
        color: $light-foreground-4; // #93A0AF - Figma'daki gray text color
      }

      .value {
        font-family: 'Plus Jakarta Sans', sans-serif;
        font-weight: 600;
        font-size: $body-3-size;
        line-height: 1.2;
        color: $light-foreground-1; // #171D22 - Figma'daki main text color
      }

      .value-with-icon {
        display: flex;
        align-items: center;
        align-self: stretch;
        gap: 8px;

        .copy-btn {
          width: 24px;
          height: 24px;
          color: $light-foreground-1; // #171D22 - Figma'daki icon color

          &:hover {
            background-color: rgba(247, 248, 251, 0.8);
          }
        }
      }
    }
  }
}

.collection-detail-metrics {
  display: flex;
  flex-direction: column;
  align-self: stretch;
  gap: 16px;
  padding: 24px;
  flex: 1;
}

.data-cards {
  display: flex;
  justify-content: stretch;
  align-items: stretch;
  align-self: stretch;
  gap: 12px;

  .data-card {
    display: flex;
    flex-direction: column;
    flex: 1;
    background-color: $light-surface-1;
    border-radius: 12px;
    overflow: hidden;

    .card-header {
      display: flex;
      align-items: center;
      align-self: stretch;
      gap: 8px;
      padding: 8px 12px;
      border-bottom: 1px solid $light-border;

      .card-title {
        font-family: 'Plus Jakarta Sans', sans-serif;
        font-weight: 600;
        font-size: $body-3-size;
        line-height: 1.2;
        color: $light-foreground-3; // #5C6A75 - Figma'daki card title color
        margin: 0;
        flex: 1;
      }
    }

    .card-content {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      align-self: stretch;
      gap: 10px;
      padding: 12px;

      .card-value {
        font-family: 'Plus Jakarta Sans', sans-serif;
        font-weight: 700;
        font-size: $h5-size;
        line-height: 1.4;
        color: $light-foreground-1; // #171D22 - Figma'daki main value color
      }
    }
  }
}

.source-accordion {
  border: 1px solid $light-border;
  border-radius: 12px;
  overflow: hidden;

  &:not(:last-child) {
    margin-bottom: 16px;
  }

  .accordion-header {
    display: flex;
    flex-direction: column;
    align-self: stretch;
    gap: 10px;
    padding: 8px 12px;
    background-color: $light-surface-1; // #F7F8FB - Figma'daki header background
    border-bottom: 1px solid $light-border;

    .source-info {
      display: flex;
      justify-content: space-between;
      align-items: center;
      align-self: stretch;
      gap: 157px;

      .source-title-section {
        display: flex;
        flex-direction: column;
        gap: 2px;

        .title-with-tag {
          display: flex;
          align-items: center;
          align-self: stretch;
          gap: 8px;

          .source-name {
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-weight: 700;
            font-size: $body-2-size;
            line-height: 1.4;
            color: $light-foreground-1; // #171D22 - Figma'daki source name color
            margin: 0;
          }

          .source-tag {
            height: 24px;
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-weight: 600;
            font-size: $caption-1-size;
            line-height: 1.2;
            padding: 4px 8px;
            background-color: $light-surface-2 !important; // #FFFFFF - Figma'daki white background
            color: $light-foreground-1 !important; // #171D22 - Figma'daki text color
            border-radius: 200px !important; // Figma'daki rounded corners
          }
        }

        .source-id {
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-weight: 400;
          font-size: $body-3-size;
          line-height: 1.2;
          color: $light-foreground-4; // #93A0AF - Figma'daki source id color
        }
      }

      .source-actions {
        display: flex;
        align-items: center;
        gap: 8px;

        .action-btn {
          width: 24px;
          height: 24px;
          color: $light-foreground-1; // #171D22 - Figma'daki action button color
          background-color: $light-surface-2; // White background

          &:hover {
            background-color: rgba(247, 248, 251, 0.8);
          }
        }
      }
    }
  }

  .accordion-content {
    display: flex;
    flex-direction: column;
    align-self: stretch;
    gap: 12px;
    padding: 12px;
    background-color: $light-surface-2; // #FFFFFF - Figma'daki content background

    .source-details {
      display: flex;
      justify-content: stretch;
      align-items: stretch;
      align-self: stretch;
      gap: 24px;

      .detail-row {
        display: flex;
        flex-direction: column;
        gap: 4px;
        flex: 1;
        width: 33%;
        min-width: 33%;
        max-width: 33%;
        &:first-child {
          justify-content: center;
          height: 24px;
        }

        .detail-label {
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-weight: 400;
          font-size: $body-3-size;
          line-height: 1.2;
          color: $light-foreground-4; // #93A0AF - Figma'daki detail label color
        }

        .detail-value {
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-weight: 600;
          font-size: $body-3-size;
          line-height: 1.2;
          color: $light-foreground-1; // #171D22 - Figma'daki detail value color
        }

        .urls-section {
          display: flex;
          align-self: stretch;
          gap: 4px;

          .url-numbers {
            display: flex;
            flex-direction: column;
            gap: 0;

            .url-number {
              font-family: 'Plus Jakarta Sans', sans-serif;
              font-weight: 600;
              font-size: $body-3-size;
              line-height: 1.6;
              color: $light-foreground-4; // #93A0AF - Figma'daki number color
            }
          }

          .url-list {
            display: flex;
            flex-direction: column;
            gap: 0;
            flex: 1;

            .url-link {
              font-family: 'Plus Jakarta Sans', sans-serif;
              font-weight: 600;
              font-size: $body-3-size;
              line-height: 1.6;
              color: $light-info; // #4789F7 - Figma'daki link color
              text-decoration: none;
              text-overflow: ellipsis;
              width: calc(80%);
              height: 22.4px;
              overflow: hidden;
              &:hover {
                text-decoration: underline;
              }
            }
          }
        }

        .documents-section {
          display: flex;
          flex-direction: column;
          gap: 8px;

          .document-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 8px 12px;
            background-color: $light-surface-1;
            border-radius: 8px;

            .document-name {
              font-family: 'Plus Jakarta Sans', sans-serif;
              font-weight: 600;
              font-size: $body-3-size;
              line-height: 1.6;
              color: $light-foreground-1;
            }

            .document-size {
              font-family: 'Plus Jakarta Sans', sans-serif;
              font-weight: 400;
              font-size: $caption-1-size;
              line-height: 1.2;
              color: $light-foreground-4;
            }
          }
        }
      }
    }
  }
}

.success-toast {
  position: fixed;
  top: 24px;
  right: 24px;
  width: 384px;
  z-index: 1000;

  .toast-content {
    background-color: $light-foreground-1;
    border: 2px solid #CCEEDC;
    border-radius: 12px;
    padding: 16px;
    display: flex;
    align-items: center;
    gap: 16px;

    .toast-icon {
      width: 32px;
      height: 32px;
      background-color: #00AC4F;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .toast-text {
      flex: 1;

      .toast-title {
        font-family: 'Plus Jakarta Sans', sans-serif;
        font-weight: 700;
        font-size: $body-2-size;
        line-height: 1.4;
        color: $light-surface-1;
        margin: 0 0 4px 0;
      }

      .toast-message {
        font-family: 'Plus Jakarta Sans', sans-serif;
        font-weight: 400;
        font-size: $body-3-size;
        color: rgba(255, 255, 255, 0.8);
        margin: 0;
      }
    }

    .toast-close {
      color: rgba(255, 255, 255, 0.7);
      flex-shrink: 0;
    }
  }
}
</style>
